import 'mapbox-gl/dist/mapbox-gl.css';
import { memo, useEffect } from 'react';
import { MapMouseEvent, Map as MapboxMap, ViewStateChangeEvent, useMap } from 'react-map-gl/mapbox';
import { useRecoilState, useRecoilValue } from 'recoil';
import { GPT_DETAIL_PAGE } from '../../constants/app';
import { environment } from '../../environments/environment';
import { useDebounceFunction } from '../../hooks/useDebounce';
import { pageState, sidenavIsPinnedState } from '../../storage/app';
import { mapPaddingState, mapStyleState, mapViewState } from '../../storage/map';
import { MapControls } from './MapControls';
import { MapOptions } from './MapOptions';
import GptEventPathLayers from './layers/GptEventPathLayers';
import { TrafficLayer } from './layers/TrafficLayer';
import GptEventAlarmsMarkers from './markers/GptEventAlarmsMarkers';
import GptEventEngineOffMarkers from './markers/GptEventEngineOffMarkers';
import './styles/mapbox.css';

function MapWrapper() {
  const { map } = useMap();
  const [viewState, setViewState] = useRecoilState(mapViewState);
  const mapStyle = useRecoilValue(mapStyleState);
  const sidenavIsPinned = useRecoilValue(sidenavIsPinnedState);
  const mapPadding = useRecoilValue(mapPaddingState);
  const page = useRecoilValue(pageState);

  // Time needed for side menu animation
  const mapResizeDelayed = useDebounceFunction(function () {
    map?.resize();
  }, 300);

  const mapResize = useDebounceFunction(function () {
    map?.resize();
  }, 0);

  useEffect(() => {
    if (map) {
      queueMicrotask(() => {
        if (!sidenavIsPinned) {
          mapResizeDelayed();
          return;
        }
        mapResize();
      });
    }
  }, [map, sidenavIsPinned]);

  useEffect(() => {
    queueMicrotask(() => {
      map?.resize();
    });
  }, [map]);

  const onMoveEnd = (ev: ViewStateChangeEvent) => {
    setViewState(ev.viewState);
  };

  const onClick = async (ev: MapMouseEvent) => {
    const point = {
      lat: ev.lngLat.lat,
      lng: ev.lngLat.lng,
    };
    // const address = await getAddressFromPoint(point);
  };

  return (
    <MapboxMap
      id="map"
      reuseMaps
      mapboxAccessToken={environment.mapboxAPIKey}
      initialViewState={{ ...viewState }}
      onClick={onClick}
      mapStyle={mapStyle}
      onMoveEnd={onMoveEnd}
      projection={{ name: 'mercator' }}
      padding={{
        left: mapPadding,
        bottom: 0,
        right: 0,
        top: 0,
      }}
      style={{ position: 'absolute' }}
      scrollZoom={true}
      keyboard={true}
      doubleClickZoom={true}
      dragRotate={false}
      minZoom={0}
      maxZoom={18}
    >
      <TrafficLayer />
      <MapOptions />
      <MapControls />
      {page === GPT_DETAIL_PAGE.id && (
        <>
          <GptEventEngineOffMarkers />
          <GptEventAlarmsMarkers />
          <GptEventPathLayers />
        </>
      )}
    </MapboxMap>
  );
}

export default memo(MapWrapper);
